/**
 * 요양시설(common) 월별 임금설정 — 카드·필드 정의 (방문요양 UI 패턴)
 */

export interface CommonWagePayrollFieldDef {
  key: string;
  label: string;
  tooltip?: string;
  /** 금액(콤마) */
  money?: boolean;
  /** 연·야·휴 시간 입력 */
  premiumHours?: boolean;
  hoursKey?: 'cwhd_overtime_hours' | 'cwhd_night_hours' | 'cwhd_holiday_hours';
  readonly?: boolean;
  /** 자동계산 없음 — 체크박스 없이 항상 수동 입력 */
  manualOnly?: boolean;
}

export const COMMON_WAGE_INSURANCE_FIX_TOOLTIP = [
  '체크하면 금액 설정 창이 열립니다.',
  '자동계산 금액, 면제 조건을 뺀 요율 금액, 직접 입력 중에서 고릅니다.',
  '체크를 끄면 다시 자동계산됩니다.',
].join('\n');

/** 사용자용 — 상단 「자동계산」 스위치 */
export const COMMON_WAGE_USER_HELP_AUTO_CALC = [
  '켜두면 시급·근무시간·공제 요율을 바꿀 때',
  '급여와 공제 금액이 바로 다시 맞춰집니다.',
  '「계산」 버튼을 누를 필요가 없어요.',
  '',
  '끄면 숫자를 바꾼 뒤 「계산」 버튼을 눌러야',
  '한 번에 맞춰집니다.',
].join('\n');

/** 사용자용 — 근무정보 「계산」 */
export const COMMON_WAGE_USER_HELP_WORK_CALC = [
  '시급과 근무시간을 넣은 뒤 「계산」을 누르면,',
  '기본급·연장·야간·휴일 수당이 시급 기준으로 채워집니다.',
  '',
  '금액 칸을 직접 고치면 그 값이 우선합니다.',
  '자동계산이 켜져 있으면 「계산」 없이도 따라 바뀝니다.',
].join('\n');

/** 관리자(권한 80+) — 산출 방식 미저장 시 기본값 */
export const COMMON_WAGE_ADMIN_HELP_BASIC_CALC_MODE = [
  '산출 방식이 저장되어 있지 않으면 「기본급→시간」이 기본입니다.',
  '시급과 기본급을 입력하면 근무시간이 소수점 1자리로 계산됩니다.',
  '이미 저장된 방식이 있으면 그 값을 그대로 사용합니다.',
].join('\n');

/** 사용자용 — 공제 항목 옆 체크박스 + 「계산」 */
export const COMMON_WAGE_USER_HELP_MANUAL_CHECKBOX = [
  '항목 옆 체크박스를 켜면 금액 설정 창이 열립니다.',
  '자동계산된 금액과, 면제 조건 없이 요율만 적용한 금액 중 하나를 고르거나 직접 입력할 수 있어요.',
  '고른 금액은 프로그램이 자동으로 덮어쓰지 않습니다.',
  '',
  '체크를 끄면,',
  '4대보험·공제는 요율과 면제 조건에 맞게 다시 계산됩니다.',
  '',
  '자동계산이 켜져 있으면 「계산」 없이도',
  '입력을 바꿀 때마다 금액이 저절로 따라갑니다.',
].join('\n');

/** 근무정보 — 기본급 + 연·야·휴(과세) */
export const COMMON_WAGE_WORK_INFO_FIELDS: CommonWagePayrollFieldDef[] = [
  { key: 'cwhd_basic', label: '기본급', money: true, readonly: true, tooltip: '시급 × 근무시간 (또는 기본급 입력 시 시간 역산)' },
  {
    key: 'cwhd_overtime',
    label: '연장수당',
    money: true,
    readonly: true,
    premiumHours: true,
    hoursKey: 'cwhd_overtime_hours',
    tooltip: '시급 × 1.5 × 연장시간',
  },
  {
    key: 'cwhd_night',
    label: '야간수당',
    money: true,
    readonly: true,
    premiumHours: true,
    hoursKey: 'cwhd_night_hours',
    tooltip: '시급 × 0.5 × 야간시간',
  },
  {
    key: 'cwhd_holiday',
    label: '휴일수당',
    money: true,
    readonly: true,
    premiumHours: true,
    hoursKey: 'cwhd_holiday_hours',
    tooltip: '시급 × 1.5 × 휴일시간',
  },
];

/** 비과세 — 중식비·차량보조비·양육비 (방문요양 라벨 통일) */
export const COMMON_WAGE_NON_TAX_INPUT_FIELDS: CommonWagePayrollFieldDef[] = [
  { key: 'cwhd_lunch', label: '중식비(비과세)', money: true, tooltip: '월 20만원 한도' },
  { key: 'cwhd_car', label: '차량보조비(비과세)', money: true, tooltip: '자가운전 보조금 (비과세 월 20만원 한도)' },
  { key: 'cwhd_child', label: '양육비(비과세)', money: true, tooltip: '자녀 양육비 지원 (비과세 월 20만원 한도)' },
];

/** 생산직 비과세 — 연·야·휴 비과세 (표시만) */
export const COMMON_WAGE_PREMIUM_NONTAX_FIELDS: CommonWagePayrollFieldDef[] = [
  { key: 'cwhd_overtime_nontax', label: '연장(비과세)', money: true, readonly: true },
  { key: 'cwhd_night_nontax', label: '야간(비과세)', money: true, readonly: true },
  { key: 'cwhd_holiday_nontax', label: '휴일(비과세)', money: true, readonly: true },
];

/** 과세 수당 */
export const COMMON_WAGE_TAXABLE_OTHER_FIELDS: CommonWagePayrollFieldDef[] = [
  { key: 'cwhd_dayoff', label: '연차수당', money: true },
  { key: 'cwhd_senior', label: '장기근속', money: true },
  { key: 'cwhd_sunim', label: '선임수당', money: true },
  { key: 'cwhd_bonus', label: '상여금', money: true },
  { key: 'cwhd_bosu', label: '교육비', money: true },
  { key: 'cwhd_bojon', label: '보존수당', money: true },
  { key: 'cwhd_other', label: '기타수당', money: true },
  { key: 'cwhd_dang', label: '당직수당(비과세)', money: true },
];

/** 4대보험 — 국민·건강·장기 (근로·기관 동일 금액) */
export const COMMON_WAGE_INSURANCE_SHARED = [
  { key: 'yeon', label: '국민연금', laborKey: 'cwhd_l_yeon', corpKey: 'cwhd_c_yeon', tooltip: '과세임금 × 국민연금요율' },
  { key: 'gun', label: '건강보험', laborKey: 'cwhd_l_gun', corpKey: 'cwhd_c_gun', tooltip: '과세임금 × 건강보험요율' },
  { key: 'jang', label: '장기요양', laborKey: 'cwhd_l_jang', corpKey: 'cwhd_c_jang', tooltip: '건강보험료 × 장기요양요율' },
] as const;

/** 고용보험 — 근로·기관 분리 (대표자/65세 이상 등 면제 조건이 다름) */
export const COMMON_WAGE_EMPLOYMENT_INSURANCE = {
  key: 'go',
  label: '고용보험',
  laborKey: 'cwhd_l_go',
  corpKey: 'cwhd_c_go',
  tooltip: '과세임금 × 고용보험요율',
} as const;

/** 고용보험 근로/기관 분리 표시용 */
export const COMMON_WAGE_EMPLOYMENT_SIDES: { key: 'labor' | 'corp'; label: string; chipLabel: string; fieldKey: 'cwhd_l_go' | 'cwhd_c_go'; manualKey: 'l_go' | 'c_go' }[] = [
  { key: 'labor', label: '고용보험', chipLabel: '근로', fieldKey: 'cwhd_l_go', manualKey: 'l_go' },
  { key: 'corp', label: '고용보험', chipLabel: '기관', fieldKey: 'cwhd_c_go', manualKey: 'c_go' },
];

/** 근로자공제 — 4대보험 외 */
export const COMMON_WAGE_LABOR_OTHER_FIELDS: CommonWagePayrollFieldDef[] = [
  { key: 'cwhd_l_so', label: '소득세', money: true, tooltip: '간이세액표 기준' },
  { key: 'cwhd_l_ji', label: '지방세', money: true, tooltip: '소득세 × 10%' },
  { key: 'cwhd_l_jiwon', label: '지원공제', money: true },
  { key: 'cwhd_l_sik', label: '중식비공제', money: true },
  { key: 'cwhd_l_etc', label: '기타공제', money: true, manualOnly: true },
  { key: 'cwhd_l_yearend', label: '연말정산', money: true, manualOnly: true },
];

/** 기관공제 — 4대보험 외 */
export const COMMON_WAGE_CORP_OTHER_FIELDS: CommonWagePayrollFieldDef[] = [
  { key: 'cwhd_c_sil', label: '실업급여', money: true },
  { key: 'cwhd_c_san', label: '산재보험', money: true },
  { key: 'cwhd_c_out', label: '퇴직금', money: true, tooltip: '총 지급금액 ÷ 12. 중식 실비변상 시 한도까지 채운 회계 중식비 제외' },
];

/** 하단 참고 바 (방문요양 센터장/사복 기준) */
export const COMMON_WAGE_REFERENCE_FIELDS = [
  { key: 'cwhd_wage_total', label: '지급금액', tooltip: '과세임금 + 비과세액 = 총 지급액' },
  { key: 'netPay', label: '실수령액', tooltip: '지급금액 - 근로자공제합계' },
] as const;
